import React from "react";
import { useSearchParams } from "react-router-dom";
import { AnalyticsTabsLayout } from "@/components/pages/analytics/components/AnalyticsTabsLayout";
import {
  ANALYTICS_TABS,
  DEFAULT_ANALYTICS_TAB,
  isAnalyticsTab,
  type AnalyticsTab,
} from "@/components/pages/analytics/config/analyticsTabs";
import { clearInactiveAnalyticsSubTabParams } from "@/components/pages/analytics/config/analyticsSubTabs";
import { useAnalyticsPage } from "@/components/pages/analytics/front-desk/context/AnalyticsPageContext";
import { AnalyticsTabExportProvider } from "@/components/pages/analytics/front-desk/context/AnalyticsTabExportContext";
import { OverviewFiltersBar } from "@/components/pages/analytics/front-desk/components/overview/OverviewFiltersBar";
import { AdminOffersTab } from "./components/offers/AdminOffersTab";
import { AdminOverviewTab } from "./components/overview/AdminOverviewTab";
import { AdminReviewsTab } from "./components/reviews/AdminReviewsTab";
import { AdminOperationsTab } from "./components/AdminOperationsTab";
import { AdminRevenueTab } from "./components/AdminRevenueTab";
import { AdminServicesTab } from "./components/AdminServicesTab";
import { AdminTeamTab } from "./components/AdminTeamTab";
import { AdminTenantGuard } from "./components/shared/AdminTenantGuard";

const TAB_PANELS: Record<AnalyticsTab, React.ComponentType> = {
  overview: AdminOverviewTab,
  revenue: AdminRevenueTab,
  services: AdminServicesTab,
  offers: AdminOffersTab,
  reviews: AdminReviewsTab,
  operations: AdminOperationsTab,
  team: AdminTeamTab,
};

function AdminAnalyticsDashboardContent(): React.JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const { dateRange, setDateRange } = useAnalyticsPage();
  const rawTab = searchParams.get("tab") ?? "";
  const activeTab: AnalyticsTab = isAnalyticsTab(rawTab)
    ? rawTab
    : DEFAULT_ANALYTICS_TAB;

  const handleTabChange = (value: string) => {
    if (!isAnalyticsTab(value)) {
      return;
    }

    setSearchParams((previousParams) => {
      const nextParams = clearInactiveAnalyticsSubTabParams(
        previousParams,
        value,
      );
      nextParams.set("tab", value);
      return nextParams;
    });
  };

  return (
    <AnalyticsTabsLayout
      tabs={ANALYTICS_TABS}
      activeTab={activeTab}
      onTabChange={handleTabChange}
      filters={
        <OverviewFiltersBar
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
        />
      }
      renderPanel={(tab) => {
        const Panel = TAB_PANELS[tab];
        return (
          <AdminTenantGuard>
            <Panel />
          </AdminTenantGuard>
        );
      }}
    />
  );
}

export const AdminAnalyticsDashboard = (): React.JSX.Element => {
  return (
    <AnalyticsTabExportProvider>
      <AdminAnalyticsDashboardContent />
    </AnalyticsTabExportProvider>
  );
};
